import { apiClient } from './client';

/**
 * 근태(출퇴근) API client — backend `apps/chat-server/src/modules/hr/attendance.routes.ts`.
 *
 * `api/hr.ts` 는 휴가·출장·초과근무 "신청" 만 다룬다. 출퇴근은 승인 대상이 아니라 **사실 기록**
 * 이라 라우트 계약도 다르므로(상태·결재자 없음) 파일을 나눠 둔다.
 *
 * 소비처: ⑥ 상태바 근무 상태 드롭다운(StatusDropdown) · 대시보드 근태 위젯 · 내 근태 화면.
 * 셋 다 `store/workStatusStore` 를 거쳐 **같은 기록**을 읽고 쓴다 — 2026-08-23 이관 전에는
 * 위젯만 브라우저 저장을 봐서 같은 출퇴근이 화면마다 달랐다.
 */

export interface AttendanceDto {
  /** YYYY-MM-DD (KST 근무일). */
  workDate: string;
  /** ISO8601. 미기록이면 null. */
  checkInAt: string | null;
  checkOutAt: string | null;
}

/**
 * 간주 출근 — **승인된 출장·외근일은 출근한 것으로 본다** (근태 D2, 2026-08-24).
 *
 * 결재가 끝나도 근태로 돌아오는 길이 없어서, 밖에서 일한 날이 "출근 기록 없음" 으로 남았다.
 * 그날은 퇴근이 열리고 요약에서 결근으로 빠지지 않는다. **옛 서버는 이 칸을 안 준다.**
 */
export interface DeemedDto {
  kind: 'business_trip' | 'outside_work';
  destination: string;
  /** 근거가 된 신청 id. */
  tripId: string;
}

/**
 * 근무 상태 이력 한 줄 — **무슨 일이 있었나** (2026-09-06 · 판 C). **옛 서버는 안 준다.**
 *
 * ★ `at` 은 **벽시계** `HH:MM` 이다(서버가 이미 펴서 준다).
 * ★ `source` 는 「누가 남긴 것인가」다 — `RECORD` 사실 기록 · `SELF` 본인 · `CLOSED` 소정 종료 마감.
 *   안 적으면 실측으로 찍은 퇴근과 마감으로 채운 퇴근이 **같은 무게**로 읽힌다.
 */
export interface WorkStatusLogEntryDto {
  at: string;
  kind: 'CHECK_IN' | 'CHECK_OUT' | 'CHECK_OUT_CANCEL' | 'STATUS';
  /** `kind='STATUS'` 일 때만. `WorkStatusKey` 와 같은 낱말(단 `off` 는 없다 — 퇴근은 사건이다). */
  status?: 'work' | 'meeting' | 'away' | 'out' | 'trip';
  source: 'RECORD' | 'SELF' | 'CLOSED';
}

export interface TodayAttendance extends AttendanceDto {
  /** 오늘이 간주 출근일이면 그 근거. 아니면(또는 옛 서버면) null. */
  deemed: DeemedDto | null;
  /**
   * 지금 근무 상태 — 이력의 **마지막 말** (2026-09-06 · 판 C). **옛 서버는 안 준다.**
   * ★ 없으면 «모른다» 다. `work` 로 지어내지 않는다 — 출근했다는 사실은 위 두 칸이 말한다.
   */
  status: 'work' | 'meeting' | 'away' | 'out' | 'trip' | null;
}

/** 오늘 기록. 미기록도 200 + null 필드로 온다(404 아님). */
export async function getTodayAttendance(): Promise<TodayAttendance> {
  const { data } = await apiClient.get<{
    attendance: AttendanceDto; deemed?: DeemedDto | null; status?: TodayAttendance['status'];
  }>('/hr/attendance/today');
  return { ...data.attendance, deemed: data.deemed ?? null, status: data.status ?? null };
}

/**
 * 근무 상태 — 회의중 · 자리비움 · 외근 · 출장 (2026-09-06 · 판 C).
 *
 * ★★ 종전에는 **브라우저에만** 남았다(`lib/work-status.ts` · localStorage). 기기를 바꾸면
 *    사라지고 「그날 무슨 일이 있었나」는 어디에도 없었다.
 * ★ **옛 서버에는 이 라우트가 없다**(404) — 부르는 쪽이 그 실패를 삼키고 종전대로 로컬만 쓴다.
 * ★ 출근 전에는 서버가 400 `NOT_CHECKED_IN` 을 준다(간주 출근일은 열린다).
 */
export async function setWorkStatus(
  status: 'work' | 'meeting' | 'away' | 'out' | 'trip',
): Promise<{ status: TodayAttendance['status'] }> {
  const { data } = await apiClient.post<{ status: TodayAttendance['status'] }>(
    '/hr/attendance/status', { status },
  );
  return data;
}

/**
 * 퇴근 취소 — **같은 날 안에서만** (2026-09-06 · 판 C · 시안 질의 ⑧).
 *
 * ★★★ 종전에는 잘못 찍은 퇴근을 되돌릴 길이 **아예 없었다** — 「근무시간 수정」 결재를 태워야
 *    고쳐졌다. 지난 날은 여전히 그 길이다(그날 근로가 이미 셈에 들어갔다).
 * ★ 오늘 퇴근 기록이 없으면 서버가 400 `NOT_CHECKED_OUT` 을 준다.
 */
export async function cancelCheckOut(): Promise<{
  attendance: AttendanceDto; status: TodayAttendance['status'];
}> {
  const { data } = await apiClient.post<{ attendance: AttendanceDto; status?: TodayAttendance['status'] }>(
    '/hr/attendance/check-out/cancel',
  );
  // ★ 되돌리면 **퇴근 앞의 상태로 돌아간다**(회의중이었으면 회의중이다) — 서버가 그것을 답한다.
  return { attendance: data.attendance, status: data.status ?? null };
}

/** 출근. 멱등 — 이미 출근했으면 첫 시각을 그대로 돌려준다(`alreadyCheckedIn=true`). */
export async function checkInAttendance(): Promise<{ attendance: AttendanceDto; alreadyCheckedIn: boolean }> {
  const { data } = await apiClient.post<{ attendance: AttendanceDto; alreadyCheckedIn: boolean }>(
    '/hr/attendance/check-in',
  );
  return data;
}

/**
 * 퇴근. 오늘 출근 기록이 없으면 서버가 400 `NOT_CHECKED_IN` 을 준다.
 * **간주 출근일(승인된 출장·외근)은 예외** — 출근을 안 찍었어도 열린다 (2026-08-24).
 */
export async function checkOutAttendance(): Promise<AttendanceDto> {
  const { data } = await apiClient.post<{ attendance: AttendanceDto }>('/hr/attendance/check-out');
  return data.attendance;
}

/** 이번 달 근태 요약. 판정 기준(`lateThreshold`)도 함께 온다 — 화면이 "무엇을 기준으로 지각인가" 를 밝힐 수 있게. */
export interface AttendanceSummaryDto {
  /** 실측 출근일 + **간주 출근일**(승인된 출장·외근). 2026-08-24 부터 간주가 포함된다. */
  workedDays: number;
  lateDays: number;
  leaveDays: number;
  /** 그중 간주로 든 날. **옛 서버는 안 준다.** */
  deemedDays?: number;
  year: number;
  month: number;
  /** `HH:MM`. 지금은 **회사 기본값 하나**다 — 사람마다 다른 근무제 시각은 근태 2단계에서 붙는다. */
  lateThreshold: string;
  /**
   * 오늘 정책이 지각을 **판정하나** (서버 `attendance.routes.ts` 요약 응답에 이미 있는 칸 · 2026-09-16 타입에 더함).
   * ★ 옛 서버는 안 준다(`undefined`) — 그때는 «모른다»로 두고 지각 수를 그대로 적는다.
   */
  lateJudged?: boolean;
  tzOffsetMinutes: number;
}

/** 그 달의 근무일·지각·휴가. month 는 1..12(생략하면 이번 달). */
export async function getAttendanceSummary(year?: number, month?: number): Promise<AttendanceSummaryDto> {
  const { data } = await apiClient.get<AttendanceSummaryDto>('/hr/attendance/summary', {
    params: year && month ? { year, month } : undefined,
  });
  return data;
}

// ─── 일별 집계 · 마감 (근태 D1, 2026-08-24) ─────────────────────────
//
// 요약(summary)이 "며칠" 을 센다면 여기는 **그 며칠이 각각 무엇이었나** 를 준다.
// ★ 옛 서버에는 이 두 라우트가 **없다**(404). 부르는 쪽이 실패를 삼키고 그 절을 감춘다 —
//   그래야 서버보다 프론트가 먼저 올라가도 화면이 깨지지 않는다.

export type DailyStatus = 'NONE' | 'LEAVE' | 'DEEMED' | 'OPEN' | 'MISSING_CHECKOUT' | 'CLOSED';

export interface DailyWorkDto {
  /** YYYY-MM-DD. */
  date: string;
  status: DailyStatus;
  /** 그날의 기준 출근·소정 종료 `HH:MM`. */
  baseStart: string;
  baseEnd: string;
  /** 체류(분) — 출근~퇴근. */
  stayMinutes: number;
  /** 인정 근로(분) — **소정을 넘지 않는다**(연장은 승인분만). */
  creditedMinutes: number;
  /** 소정 종료를 넘겨 남았으나 승인되지 않은 시간(분). 기록만 한다. */
  unclaimedMinutes: number;
  overtimeMinutes: number;
  breakMinutes: number;
  /** 지난 날인데 출근만 있고 퇴근이 없다. */
  needsClose: boolean;
  /**
   * 그날 **찍은 시각** `HH:MM` (2026-09-06 · 판 B). **옛 서버는 안 준다.**
   *
   * ★ 종전에는 「몇 시간 일했나」만 왔다 — 그래서 화면은 「8시간」은 알아도
   *   **「몇 시에 왔다 갔나」는 몰랐다**(달력 칸이 그것을 적는다).
   * ★ 안 찍은 날은 `null` 이다 — 「--:--」 은 **화면이 고른 표기**이지 서버의 값이 아니다.
   */
  checkIn?: string | null;
  checkOut?: string | null;
  /**
   * 그날 **승인된 연장의 시각 구간** (2026-09-06 · 판 B). **옛 서버는 안 준다.**
   * ★ 분만으로는 하루 띠에서 **어디였는지** 그릴 수 없다 — 종전에는 이 값을 얻으려고
   *   `/hr/my-wage` 를 한 번 더 불렀다.
   */
  overtimeSpans?: Array<{ start: string; end: string }>;
  /** 가산 시간(근태 E1). **옛 서버엔 없다** — 없으면 그 줄은 가산이 없는 날이거나 옛 서버다. */
  premium?: PremiumMinutesDto;
  /**
   * 그날 지각이었나 (2026-09-03 · 개인 근태 P3-3). **옛 서버엔 없다.**
   * 없으면 «모른다» 로 둔다 — false 로 지어내면 「지각이 아니었다」는 거짓말이 된다.
   */
  late?: boolean;
  /**
   * 그날 **근무 상태내역** (2026-09-06 · 판 C). **옛 서버엔 없다.**
   *
   * ★ 이력 행 + **사실 기록에만 있는 것**을 서버가 합쳐 준다 — 이 표가 생기기 전의 날들이
   *   「그날 아무 일도 없었다」로 읽히면 안 된다.
   * ★ 그릴 것이 하나도 없으면 **칸 자체가 없다** — 빈 배열은 「기록이 없다」와 「옛 서버라
   *   모른다」를 못 가른다.
   */
  statusLog?: WorkStatusLogEntryDto[];
}

/**
 * 근태가 급여에 넘기는 **세 숫자**(근태 E1) — 금액은 여기 없다.
 *
 * ★★ **더하지 말 것.** 야간은 연장·휴일과 **겹친다**(22:00~24:00 의 연장 2시간은 연장이면서
 *    야간이다). 평일 연장과 휴일근로만 서로 겹치지 않는다.
 */
export interface PremiumMinutesDto {
  /** 평일 연장(분) — × 1.5. */
  overtimeMinutes: number;
  /** 휴일근로 8시간 이내(분) — × 1.5. */
  holidayMinutes: number;
  /** 휴일근로 8시간 초과(분) — × 2.0. */
  holidayOverMinutes: number;
  /** 야간(분) — 위와 겹친다. +0.5. */
  nightMinutes: number;
}

export interface DailyWorkMonthDto {
  year: number;
  month: number;
  days: DailyWorkDto[];
  /**
   * 그 달의 **쉬는 날** `YYYY-MM-DD` 목록 (2026-09-11 · A-3).
   *
   * ★★★ 기록이 없는 날은 `days` 에 **아예 없다** — 그래서 화면이 「휴일」인지 「기록 없음」인지
   *    스스로 판정하려고 **토·일을 코드에 박고** 있었다. 주 6일 회사의 토요일이 「휴일」로
   *    적혔다. 이제 서버가 그 사람의 근무요일·회사 휴일·교대 편성을 보고 답한다.
   * ★ 옛 서버는 이 칸을 안 준다(`undefined`) — 그때는 종전처럼 토·일로 본다.
   */
  restDates?: string[];
  total: {
    creditedMinutes: number;
    overtimeMinutes: number;
    unclaimedMinutes: number;
    needsCloseDays: number;
    deemedDays: number;
  };
  /** 달 가산 시간 합계 — 옛 서버엔 없다(그러면 화면이 그 절을 감춘다). */
  premiumTotal?: PremiumMinutesDto;
  /** 무엇을 휴일로 셌는지 — 기준을 감추면 판정을 믿을 수 없다. */
  premiumBasis?: {
    nightWindow: string;
    holidayDates: number;
    /** 주휴일(주말)을 셌는가 — 지금은 담을 칸이 없어 false 다. */
    weeklyRestCounted: boolean;
  };
  tzOffsetMinutes: number;
}

/** 그 달의 날별 집계. month 는 1..12(생략하면 이번 달). */
export async function getDailyWork(year?: number, month?: number): Promise<DailyWorkMonthDto> {
  const { data } = await apiClient.get<DailyWorkMonthDto>('/hr/attendance/daily', {
    params: year && month ? { year, month } : undefined,
  });
  return data;
}

/**
 * 안 닫힌 날을 **소정 종료 시각으로** 마감한다.
 *
 * ★ 서버가 몰래 닫지 않는다 — 누른 것이 사람의 확인이라 사실이 된다. 이미 닫힌 날은
 *   덮어쓰지 않고 `alreadyClosed=true` 로 온다.
 */
export async function closeWorkDay(workDate: string): Promise<{
  attendance: AttendanceDto; alreadyClosed: boolean; closedAt?: string;
}> {
  const { data } = await apiClient.post<{ attendance: AttendanceDto; alreadyClosed: boolean; closedAt?: string }>(
    '/hr/attendance/close-day', { workDate },
  );
  return data;
}
